<template>
  <div class="email-form">
    <mf-card-container>
      <template v-slot:title-complement>
        <span class="title d-flex align-center">
          Configuração E-mail
          <v-icon class="ml-2 channel-icon">mdi-email</v-icon>
        </span>
      </template>

      <div class="mt-10 email-subject">
        <label for="">Assunto do E-mail</label>
        <v-text-field v-model="subject" dense outlined height="40" :error-messages="subjectInputErrorMessage" />
      </div>
    </mf-card-container>

    <mf-card-container class="mt-5" title="Mensagem">
      <template v-slot:headers>
        <div class="d-flex align-self-end" style="gap: 16px">
          <best-offer-configuration-dialog channel="email" :offer-type="selectedOfferType" @updated-variables="handleVariablesUpdate" />
          <template-message-modal v-model="selectedTemplate" :mode="['EMAIL']" />
        </div>
      </template>

      <mf-tabs-menu :key="componentKey" v-model="templateTab" :tabs="tabsMenuItems" class="my-4" />

      <!-- Simple Email Composer -->
      <simple-email-composer
        v-if="templateTab === 0"
        ref="simple-email-composer"
        :message="getSimpleEmailMessage()"
        :settings-id="settingsId"
        @update:message="updateMessage"
        @update:title="updateTitle"
        @update:buttonText="updateButtonText"
        @update:buttonLink="updateButtonLink"
        @update:footerText="updateFooterText"
        @update:headerImage="updateHeaderImage"
        @update:bodyImage="updateBodyImage"
        @update:processedHtml="updateProcessedHtml"
      />

      <!-- Builder Email Composer -->
      <builder-email-composer
        v-if="templateTab === 1"
        ref="builder-email-composer"
        :message="channelSettings.message"
        :campaign-data="{ ...channelSettings, auto_distributed: isAutoDistributedCampaign === true, max_best_offer_model_clients: maxBestOfferModelClients }"
        :default-colors="defaultColors"
        :subject="subject"
      />

      <!-- HTML Email Composer -->
      <html-email-composer v-if="templateTab === 2" ref="html-email-composer" :message="channelSettings.message" />

      <!-- Botão de preview do email-->
      <mf-button label="Enviar preview" data-cy="send-preview-modal-btn" @click="openEmailPreview" />
    </mf-card-container>

    <!-- Modal de Preview -->
    <send-email-preview-modal ref="sendEmailPreviewModal" v-model="openDialog" :campaign="campaignForModal" />
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { channelsForm } from '@/modules/maria/campanha-da-maria-v2/mixins/channels-form'
import { QUERY_GET_DEFAULT_COLORS_HOTSITE } from '@/modules/maria/campanha-da-maria-v2/graphql'

export default {
  name: 'EmailForm',
  components: {
    BestOfferConfigurationDialog: () =>
      import('@/modules/maria/campanha-da-maria-v2/views/components/ChannelsConfiguration/BestOfferConfiguration/BestOfferConfigurationDialog.vue'),
    TemplateMessageModal: () => import('@/components/template-message/TemplateMessageModal.vue'),
    SimpleEmailComposer: () => import('./SimpleEmailComposer.vue'),
    HtmlEmailComposer: () => import('./HtmlEmailComposer.vue'),
    BuilderEmailComposer: () => import('./BuilderEmailComposer.vue'),
    SendEmailPreviewModal: () => import('@/modules/campanhas/campanhas-email/views/components/SendEmailPreviewModal.vue'),
  },
  mixins: [channelsForm],
  data: () => ({
    templateTab: 0,
    componentKey: 0,
    selectedTemplate: {},
    subject: '',
    subjectInputErrorMessage: [],
    message: '',
    title: '',
    buttonText: '',
    buttonLink: '',
    footerText: '',
    headerImage: '',
    bodyImage: '',
    processedHtml: '',
    defaultColors: null,
    openDialog: false,
    campaignForModal: null,
  }),
  computed: {
    isAutoDistributedCampaign() {
      if (!Array.isArray(this.campaign?.offer_schedules) || this.campaign.offer_schedules.length === 0) {
        return false
      }
      return this.campaign.offer_schedules.some((schedule) => {
        return schedule?.auto_distributed === true || schedule?.offer_schedule?.auto_distributed === true
      })
    },
    maxBestOfferModelClients() {
      if (!Array.isArray(this.campaign?.offer_schedules) || this.campaign.offer_schedules.length === 0) {
        return {}
      }

      const isValidObj = (obj) => obj && typeof obj === 'object' && !Array.isArray(obj) && Object.keys(obj).length > 0
      const scheduleWithConfig = this.campaign.offer_schedules.find((schedule) => {
        const direct = schedule?.max_best_offer_model_clients
        const nested = schedule?.offer_schedule?.max_best_offer_model_clients
        return isValidObj(direct) || isValidObj(nested)
      })

      if (!scheduleWithConfig) {
        return {}
      }

      return scheduleWithConfig?.max_best_offer_model_clients || scheduleWithConfig?.offer_schedule?.max_best_offer_model_clients || {}
    },
    channelSettings() {
      return this.campaignSettings?.channels?.email || {}
    },
    tabsMenuItems() {
      return [
        {
          name: 'Simples',
          type: 'SIMPLE',
          ref: 'simple-email-composer',
          disabled: this.disabled,
        },
        {
          name: 'Construtor',
          type: 'BUILDER',
          ref: 'builder-email-composer',
          disabled: this.disabled,
        },
        {
          name: 'HTML',
          type: 'HTML',
          ref: 'html-email-composer',
          disabled: this.disabled,
        },
      ]
    },
  },
  watch: {
    selectedTemplate(newValue) {
      if (newValue.message !== this.message) {
        this.message = newValue.message
        this.$emit('update:message', newValue.message)
      }
    },
    subject() {
      this.subjectInputErrorMessage = []
    },
  },
  mounted() {
    this.initializeData()
    this.initTemplateTab()
    this.fetchDefaultColors()
  },
  methods: {
    initializeData() {
      this.subject = this.channelSettings.message?.subject || ''

      const emailConfigType = this.channelSettings?.message?.email_configuration_type

      if (emailConfigType === 'SIMPLE') {
        this.message = this.channelSettings.message?.simple_email_configuration?.text || ''
        this.title = this.channelSettings.message?.simple_email_configuration?.subject || ''
        this.buttonText = this.channelSettings.message?.simple_email_configuration?.click_to_call_button_text || ''
        this.buttonLink = this.channelSettings.message?.simple_email_configuration?.click_to_call_button_link || ''
        this.footerText = this.channelSettings.message?.simple_email_configuration?.footer || ''
        this.headerImage = this.channelSettings.message?.simple_email_configuration?.header_image || ''
        this.bodyImage = this.channelSettings.message?.simple_email_configuration?.image || ''
      }

      this.processedHtml = this.channelSettings.message?.processed_html || ''
    },
    initTemplateTab() {
      const emailConfigurationType = this.channelSettings?.message?.email_configuration_type
      switch (emailConfigurationType) {
        case 'SIMPLE':
          this.templateTab = 0
          break
        case 'BUILDER':
          this.templateTab = 1
          break
        case 'HTML':
          this.templateTab = 2
          break
        default:
          this.templateTab = 0
      }
    },
    handleVariablesUpdate() {
      if (this.templateTab === 0 && this.$refs['simple-email-composer']) {
        this.$refs['simple-email-composer'].countAllCharacters()
      }
    },
    updateMessage(value) {
      this.message = value
    },
    updateTitle(value) {
      this.title = value
    },
    updateButtonText(value) {
      this.buttonText = value
    },
    updateButtonLink(value) {
      this.buttonLink = value
    },
    updateFooterText(value) {
      this.footerText = value
    },
    updateHeaderImage(value) {
      this.headerImage = value
    },
    updateBodyImage(value) {
      this.bodyImage = value
    },
    updateProcessedHtml(value) {
      this.processedHtml = value
    },
    getSimpleEmailMessage() {
      return {
        ...this.channelSettings.message,
        simple_email_configuration: {
          text: this.message || this.channelSettings.message?.simple_email_configuration?.text || '',
          subject: this.title || this.channelSettings.message?.simple_email_configuration?.subject || '',
          click_to_call_button_text: this.buttonText || this.channelSettings.message?.simple_email_configuration?.click_to_call_button_text || '',
          click_to_call_button_link: this.buttonLink || this.channelSettings.message?.simple_email_configuration?.click_to_call_button_link || '',
          footer: this.footerText || this.channelSettings.message?.simple_email_configuration?.footer || '',
          header_image: this.headerImage || this.channelSettings.message?.simple_email_configuration?.header_image || '',
          image: this.bodyImage || this.channelSettings.message?.simple_email_configuration?.image || '',
        },
        processed_html: this.processedHtml || this.channelSettings.message?.processed_html || '',
      }
    },
    async getConfiguration() {
      const currentTab = this.tabsMenuItems[this.templateTab]
      const componentRef = this.$refs[currentTab.ref]

      if (!componentRef) {
        throw new Error(`Component ${currentTab.ref} not found`)
      }

      return componentRef.getConfiguration()
    },
    async validInput() {
      this.$v.$reset()
      this.$v.$touch()

      const errors = []
      this.clearSubjectError()

      this.validateSubject(errors)

      const currentTab = this.tabsMenuItems[this.templateTab]
      const componentRef = this.$refs[currentTab.ref]

      if (componentRef && componentRef.validInput) {
        const isValid = await componentRef.validInput()
        if (!isValid) {
          errors.push(true)
        }
      }

      return errors.length === 0
    },
    clearSubjectError() {
      this.subjectInputErrorMessage = []
    },
    validateSubject(errors) {
      if (this.$v.subject.$invalid) {
        if (!this.$v.subject.required) {
          this.subjectInputErrorMessage.push('Adicione um assunto para o e-mail.')
          errors.push(true)
        }
        if (!this.$v.subject.notOnlySpace) {
          this.subjectInputErrorMessage.push('Não é permitido salvar apenas espaços.')
          errors.push(true)
        }
      }
    },
    addColorSchemeMetaTags(html) {
      if (!html || typeof html !== 'string') {
        return html
      }

      const hasColorSchemeMeta = html.includes('name="color-scheme"')
      const hasSupportedColorSchemesMeta = html.includes('name="supported-color-schemes"')

      if (hasColorSchemeMeta && hasSupportedColorSchemesMeta) {
        return html
      }

      const tagsToAdd = []
      if (!hasColorSchemeMeta) {
        tagsToAdd.push('<meta name="color-scheme" content="light">')
      }
      if (!hasSupportedColorSchemesMeta) {
        tagsToAdd.push('<meta name="supported-color-schemes" content="light">')
      }

      const metaTags = '\n    ' + tagsToAdd.join('\n    ')

      const headMatch = html.match(/<head[^>]*>/i)
      if (headMatch) {
        const headTag = headMatch[0]
        const headIndex = html.indexOf(headTag)
        const insertIndex = headIndex + headTag.length
        return html.slice(0, insertIndex) + metaTags + html.slice(insertIndex)
      }

      const htmlMatch = html.match(/<html[^>]*>/i)
      if (htmlMatch) {
        const htmlTag = htmlMatch[0]
        const htmlIndex = html.indexOf(htmlTag)
        const insertIndex = htmlIndex + htmlTag.length
        const headTag = '\n  <head>' + metaTags + '\n  </head>'
        return html.slice(0, insertIndex) + headTag + html.slice(insertIndex)
      }

      return `<!DOCTYPE html>
                <html>
                  <head>${metaTags}
                  </head>
                  <body>
                    ${html}
                  </body>
                </html>`
    },
    async getChannelData() {
      const configuration = await this.getConfiguration()
      const processedHtml = this.addColorSchemeMetaTags(configuration.processed_html)

      const channelData = {
        ...this.channelSettings,
        message: {
          ...this.channelSettings.message,
          subject: this.subject,
          email_configuration_type: this.tabsMenuItems[this.templateTab].type,
          processed_html: processedHtml,
          ...(this.tabsMenuItems[this.templateTab].type === 'SIMPLE' && {
            simple_email_configuration: {
              text: this.message,
              amount_best_offers: null,
              subject: this.title,
              amount_best_offers_subject: null,
              header_image: this.headerImage,
              image: this.bodyImage,
              click_to_call_button_text: this.buttonText,
              click_to_call_button_link: this.buttonLink,
              footer: this.footerText,
            },
          }),
          ...(this.tabsMenuItems[this.templateTab].type === 'BUILDER' && {
            builder_email_configuration: configuration.builder_email_configuration,
            ...(this.isAutoDistributedCampaign === true
              ? {
                  amount_best_offers_models: configuration.amount_best_offers_models || [],
                }
              : {
                  amount_best_offers: configuration.amount_best_offers,
                  amount_best_offers_subject: null,
                }),
          }),
          ...(this.tabsMenuItems[this.templateTab].type === 'HTML' && {
            html_email_configuration: configuration.html_email_configuration,
          }),
        },
      }
      return channelData
    },
    async fetchDefaultColors() {
      try {
        const { data } = await this.$apollo.query({
          query: QUERY_GET_DEFAULT_COLORS_HOTSITE,
        })

        if (data?.account?.channels?.hotsite?.configuration?.assets?.colors) {
          this.defaultColors = data.account.channels.hotsite.configuration.assets.colors
        }
      } catch (error) {
        this.$snackbar({
          message: 'Erro ao carregar cores padrão do hotsite',
          snackbarColor: 'error',
        })
      }
    },
    async openEmailPreview() {
      try {
        if (!this.subject || this.subject.trim() === '') {
          this.$snackbar({
            message: 'Adicione um assunto para o e-mail.',
            snackbarColor: 'error',
          })
          return
        }

        const currentTab = this.tabsMenuItems[this.templateTab]
        const componentRef = this.$refs[currentTab.ref]

        if (!componentRef) {
          this.$snackbar({
            message: 'Componente não encontrado.',
            snackbarColor: 'error',
          })
          return
        }

        let configuration = {}

        if (currentTab.type === 'BUILDER' && componentRef.getConfiguration) {
          configuration = await componentRef.getConfiguration()
        } else if (currentTab.type === 'SIMPLE') {
          configuration = {
            processed_html: this.processedHtml || this.channelSettings.message?.processed_html || '',
          }
        } else if (currentTab.type === 'HTML' && componentRef.getConfiguration) {
          configuration = await componentRef.getConfiguration()
        }

        const processedHtmlWithMeta = this.addColorSchemeMetaTags(configuration.processed_html)

        this.campaignForModal = {
          message: {
            processed_html: processedHtmlWithMeta,
            subject: this.subject.trim(),
          },
        }

        this.openDialog = true
        this.$nextTick(() => {
          if (this.$refs.sendEmailPreviewModal) {
            this.$refs.sendEmailPreviewModal.campaign = this.campaignForModal
          }
        })
      } catch (error) {
        this.$snackbar({
          message: 'Erro ao gerar preview do email',
          snackbarColor: 'error',
        })
      }
    },
  },
  validations() {
    return {
      subject: {
        required,
        notOnlySpace,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.email-subject {
  display: flex;
  width: 602px;
  flex-direction: column;
  gap: 8px;
}

.title {
  font-family: Lato;
  font-weight: 600;
  font-size: 18px !important;
  line-height: 21.6px;
  color: $marca-0-l20;
}

.channel-icon {
  color: $marca-0-l20;
  font-size: 20px;
}
</style>
